import React from 'react';
import { motion } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import { formatDistanceToNow } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiActivity, FiPlus, FiMinus, FiAlertTriangle, FiShoppingCart } = FiIcons;

const RecentActivity = ({ activities }) => {
  const { getProductById } = useInventory();

  const getActivityIcon = (type) => {
    switch (type) {
      case 'purchase': return FiPlus;
      case 'sale': return FiShoppingCart;
      case 'damaged': return FiAlertTriangle;
      case 'internal_use': return FiMinus;
      default: return FiActivity;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'purchase': return 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/30';
      case 'sale': return 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30';
      case 'damaged': return 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30';
      case 'internal_use': return 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/30';
      default: return 'text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-700';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-200">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <SafeIcon icon={FiActivity} className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h2>
        </div>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {activities.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            No recent activity
          </div>
        ) : (
          activities.map((activity, index) => {
            const product = getProductById(activity.productId);
            const icon = getActivityIcon(activity.type);
            const colorClass = getActivityColor(activity.type);

            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${colorClass}`}>
                    <SafeIcon icon={icon} className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {product ? product.name : 'Unknown Product'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm text-gray-600 dark:text-gray-400">{activity.reason}</p>
                      <span className={`text-sm font-medium ${activity.quantity > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {activity.quantity > 0 ? '+' : ''}{activity.quantity}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RecentActivity;